import {
  Card,
  Container,
  Stack,
  Input,
  Textarea,
  Button,
  SimpleGrid,
  CheckboxGroup,
  Fieldset,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useController } from 'react-hook-form';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import useCollectionTypesStore from '@/utils/use-collection-type';
import useContentTypesStore from '@/utils/use-content-type';
import api from '@/utils/api';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  libraries: z.string().array(),
});

type FormData = z.infer<typeof formSchema>;

export default function CreateCollectionType() {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const { loadCollectionTypes } = useCollectionTypesStore();
  const { contentTypes } = useContentTypesStore();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const library = useController({
    control,
    name: 'libraries',
    defaultValue: [],
  });

  const onSubmit = async (data: FormData) => {
    if (!workspaceId) {
      return;
    }

    try {
      const res = await api.post(`collection-types`, {
        name: data.name,
        description: data.description,
        workspace_id: parseInt(workspaceId),
      });

      if (res.data.id && data.libraries.length > 0) {
        data.libraries.map(async (libraryId) => {
          await api.post(`collection-content-types`, {
            workspace_id: parseInt(workspaceId, 10),
            content_type_id: parseInt(libraryId, 10),
            collection_type_id: parseInt(res.data.id, 10),
          });
        });
      }

      if (workspaceId) {
        loadCollectionTypes(workspaceId);
      }
      navigate(`/${workspaceId}/collection-types`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create content collection',
      });
    }
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'3xl'}>
            <Card.Title mb={4} fontSize={'3xl'}>
              Create custom collection
            </Card.Title>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={4}>
                <Field label='Name' invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input {...register('name')} />
                </Field>
                <Field label='Description'>
                  <Textarea {...register('description')} rows={4} />
                </Field>
                {contentTypes.length > 0 && (
                  <Fieldset.Root invalid={!!errors.libraries}>
                    <Fieldset.Legend>
                      Choose the content libraries you want to use when adding content to this
                      collection:
                    </Fieldset.Legend>
                    <CheckboxGroup
                      invalid={!!errors.libraries}
                      value={library.field.value}
                      onValueChange={library.field.onChange}
                      name={library.field.name}
                      colorPalette='brand'
                    >
                      <SimpleGrid columns={{ base: 2, md: 3 }} gap={4} pl={4}>
                        {contentTypes.map((item) => (
                          <Checkbox key={item.id} value={item.id.toString()}>
                            {item.name}
                          </Checkbox>
                        ))}
                      </SimpleGrid>
                    </CheckboxGroup>
                    {errors.libraries && (
                      <Fieldset.ErrorText>{errors.libraries.message}</Fieldset.ErrorText>
                    )}
                  </Fieldset.Root>
                )}
                <Button
                  colorPalette='brand'
                  type={'submit'}
                  width={'full'}
                  mt={5}
                  loading={isSubmitting}
                  loadingText='Loading...'
                >
                  Save
                </Button>
              </Stack>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
